/**
 * Function Call Status Component
 * Displays an animated status indicator for function calls (e.g., web search)
 * This is an unpublished work containing 3M confidential and proprietary information.
 * Disclosure, use or reproduction without the written authorization of 3M is prohibited.
 * If publication occurs, the following notice applies:
 * Copyright (C) 2025, 3M. All rights reserved.
 */

import React from 'react';
import { MagnifyingGlassIcon } from '@radix-ui/react-icons';

interface FunctionCallStatusProps {
  type: 'web_search' | 'unknown';
  message: string;
  isActive: boolean;
}

export const FunctionCallStatus: React.FC<FunctionCallStatusProps> = ({ type, message, isActive }) => {
  // Determine icon and display text based on type
  const getIcon = () => {
    switch (type) {
      case 'web_search':
        return <MagnifyingGlassIcon className="w-4 h-4" />;
      default:
        return <MagnifyingGlassIcon className="w-4 h-4" />;
    }
  };

  const getDisplayText = () => {
    return message;
  };

  if (!isActive) {
    return null; // Don't show anything if function call is complete
  }

  return (
    <div className="flex items-center gap-2 py-3 px-4 rounded-lg bg-opacity-10 bg-gray-500 dark:bg-opacity-20 mb-3">
      {/* Icon with pulse animation - adjusted for top-heavy fonts */}
      <div className="animate-pulse text-blue-600 dark:text-blue-400 flex items-center translate-y-[-1px]">
        {getIcon()}
      </div>
      
      {/* Text without animated ellipses - spinner in voice mode shows progress */}
      <div style={{ color: 'var(--color-text)', paddingTop: '0.2rem' }} className="text-sm animate-pulse">
        {getDisplayText()}
      </div>
    </div>
  );
};

export default FunctionCallStatus;
